import Link from "next/link";
import ImageComponent from "./ImageComponent";

export type ProjectLink = {
  label?: string;
  link?: string;
};

export type ProjectShortCardProps = {
  title?: string;
  image?: { url?: string; alt?: string } | null;
  projectLink?: ProjectLink;
  caseStudyLink?: ProjectLink;
  smallDescription?: string;
  type?: "website" | "social-media";
  mediaHandleName?: string;
};

const formatMediaHandle = (value?: string) => {
  const handle = value?.trim() || "";
  if (!handle) return "";
  return handle.startsWith("@") ? handle : `@${handle}`;
};

const ProjectShortCard = ({
  title,
  image,
  projectLink,
  caseStudyLink,
  smallDescription,
  type,
  mediaHandleName,
}: ProjectShortCardProps) => {
  // Full-card click: prefer case study, fall back to project link
  const cardLink = caseStudyLink?.link?.trim()
    ? caseStudyLink
    : projectLink;
  const cardHref = cardLink?.link?.trim() || "";
  const hasCardLink = Boolean(cardHref);
  const cardIsExternal = cardHref.startsWith("http");

  // Label click: prefer project link, fall back to case study
  const labelLink = projectLink?.link?.trim()
    ? projectLink
    : caseStudyLink;
  const labelHref = labelLink?.link?.trim() || "";
  const linkLabel = labelLink?.label?.trim() || "";
  const hasLabelLink = Boolean(labelHref && linkLabel);
  const labelIsExternal = labelHref.startsWith("http");

  const summary = smallDescription?.trim() || "";
  const isSocialMedia = type === "social-media";
  const mediaHandle = formatMediaHandle(mediaHandleName);

  return (
    <div className="primary_before_card_hover_effect card-hover group common_border relative flex h-full flex-col overflow-hidden bg-surface">
      {hasCardLink ? (
        <Link
          href={cardHref}
          target={cardIsExternal ? "_blank" : "_self"}
          rel={cardIsExternal ? "noopener noreferrer" : undefined}
          className="absolute inset-0 z-0"
          aria-label={cardLink?.label?.trim() || title || "View project"}
        />
      ) : null}

      {isSocialMedia ? (
        image?.url || mediaHandle ? (
          <div className="relative z-1 bg_social_media_card flex aspect-16/10! flex-col items-center justify-center gap-1 overflow-hidden py-6 pointer-events-none">
            {image?.url ? (
              <div className="h-11 w-11 [&_img]:object-contain!">
                <ImageComponent
                  url={image.url}
                  img_title={image.alt || title || "Project image"}
                  width={44}
                  height={44}
                  object_contain
                  sizes="44px"
                  groupHover
                  hoverScale
                />
              </div>
            ) : null}
            {mediaHandle ? (
              <p className="text-blackishBrown font-bold text-[15px] font-secondary">
                {mediaHandle}
              </p>
            ) : null}
          </div>
        ) : null
      ) : image?.url ? (
        <div className="relative z-1 shrink-0 overflow-hidden bg_social_media_card pointer-events-none [&_img]:aspect-16/10! [&_img]:object-contain! [&_img]:object-[center_18%]!">
          <ImageComponent
            url={image.url}
            img_title={image.alt || title || "Project image"}
            width={816}
            height={1020}
            fullWidthNaturalHeight
            sizes="(min-width: 1280px) 380px, (min-width: 768px) 50vw, min(360px, 92vw)"
            groupHover
            hoverScale
          />
        </div>
      ) : null}

      <div className="relative z-1 flex flex-1 items-center justify-between gap-3 px-4 py-3.5 pointer-events-none">
        <div className="min-w-0 flex-1 space-y-1">
          {title?.trim() ? (
            <p className="wrap-break-word font-semibold leading-snug text-blackishBrown">
              {title.trim()}
            </p>
          ) : null}
          {summary ? (
            <p className="wrap-break-word text-[11px] font-legend leading-normal tracking-[0.04em] text-secondary group-hover:text-primary">
              {summary}
            </p>
          ) : null}
        </div>
        {hasLabelLink ? (
          <Link
            href={labelHref}
            target={labelIsExternal ? "_blank" : "_self"}
            rel={labelIsExternal ? "noopener noreferrer" : undefined}
            className="pointer-events-auto relative z-2 shrink-0 pt-0.5 text-xs font-semibold text-primary whitespace-nowrap hidden group-hover:block transition-all"
          >
            {linkLabel}
          </Link>
        ) : null}
      </div>
    </div>
  );
};

export default ProjectShortCard;
